import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, ArrowLeft, ChevronLeft, ChevronRight, Clock, Download, MessageCircle, MessagesSquare, MousePointerClick, RefreshCw, Search, TrendingUp, UserRound, Users, XCircle } from "lucide-react"
import { Link } from "react-router-dom"
import { AppShell } from "@/components/app-shell"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { StatTile } from "@/components/shared/stat-tile"
import { StatusBadge } from "@/components/shared/status-badge"
import { api } from "@/lib/api"
import { queryKeys } from "@/lib/query-keys"
import type { AnalyticsSummary, ConversationDetail, ConversationListItem } from "@/lib/types"

type RangeKey = "today" | "7d" | "30d" | "custom"

function rangeDates(key: RangeKey, customFrom: string, customTo: string): { from?: string; to?: string } {
  const now = new Date()
  if (key === "today") {
    const start = new Date(now); start.setHours(0, 0, 0, 0)
    return { from: start.toISOString(), to: now.toISOString() }
  }
  if (key === "7d") return { to: now.toISOString(), from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString() }
  if (key === "30d") return { to: now.toISOString(), from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString() }
  return {
    from: customFrom ? new Date(`${customFrom}T00:00:00`).toISOString() : undefined,
    to: customTo ? new Date(`${customTo}T23:59:59`).toISOString() : undefined,
  }
}

function formatDateTime(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date)
}

function formatDuration(ms: number | null) {
  if (ms === null) return "—"
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}min ${s % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}min`
}

function shortId(id: string) {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}

function statusTone(status: ConversationListItem["status"]): "success" | "warning" | "danger" | "neutral" {
  if (status === "active") return "success"
  if (status === "abandoned") return "danger"
  if (status === "closed") return "neutral"
  return "warning"
}

function statusLabel(status: ConversationListItem["status"]) {
  if (status === "active") return "Ativa"
  if (status === "closed") return "Encerrada"
  if (status === "abandoned") return "Abandonada"
  return "Sem mensagens"
}

function BarChart({ data, height = 120 }: { data: Array<{ day: string; count: number }>; height?: number }) {
  const max = Math.max(1, ...data.map(({ count }) => count))
  if (!data.length) return <p className="muted py-6 text-center text-xs">Sem conversas neste período.</p>
  return (
    <div className="flex items-end gap-1.5" style={{ height }} role="img" aria-label={`Conversas por dia, máximo ${max}`}>
      {data.map(({ day, count }) => (
        <div key={day} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1" title={`${day}: ${count}`}>
          <div className="bg-primary/80 w-full rounded-t-md" style={{ height: `${Math.max(4, (count / max) * (height - 24))}px` }} />
          <span className="muted hidden text-[9px] first:block sm:block">{day.slice(5)}</span>
        </div>
      ))}
    </div>
  )
}

function Funnel({ summary }: { summary: AnalyticsSummary }) {
  const rows = [
    { label: "Aberturas do balão", value: summary.totals.balloonSessions, icon: MousePointerClick, rate: null as string | null },
    { label: "Conversas iniciadas", value: summary.totals.conversations, icon: MessagesSquare, rate: summary.totals.balloonSessions ? `${((summary.totals.conversations / Math.max(1, summary.totals.balloonSessions)) * 100).toFixed(0)}% do balão` : null },
    { label: "Mensagens trocadas", value: summary.totals.messages, icon: MessageCircle, rate: summary.totals.conversations ? `${summary.totals.avgMessagesPerConversation} por conversa` : null },
    { label: "Transbordos WhatsApp", value: summary.totals.whatsappClicks, icon: TrendingUp, rate: summary.totals.conversations ? `${((summary.totals.whatsappClicks / Math.max(1, summary.totals.conversations)) * 100).toFixed(0)}% das conversas` : null },
  ]
  const max = Math.max(1, ...rows.map(({ value }) => value))
  return (
    <div className="space-y-3">
      {rows.map(({ label, value, icon: Icon, rate }) => (
        <div key={label}>
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
            <span className="flex items-center gap-1.5 font-medium"><Icon className="size-3.5 text-muted-foreground" />{label}</span>
            <span className="flex items-baseline gap-2"><span className="font-bold">{value}</span>{rate && <span className="muted text-[10px]">{rate}</span>}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="bg-primary h-full rounded-full transition-all" style={{ width: `${Math.max(2, (value / max) * 100)}%` }} /></div>
        </div>
      ))}
      {summary.prepared?.balloonFunnel && (
        <p className="muted text-[11px]">Funil do balão em coleta: o rastreamento de abertura começou agora e os números crescem com os novos registros.</p>
      )}
    </div>
  )
}

export function AnalyticsPage({ chatbotId, chatbotName }: { chatbotId: string; chatbotName: string }) {
  const [range, setRange] = useState<RangeKey>("30d")
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("all")
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { from, to } = useMemo(() => rangeDates(range, customFrom, customTo), [range, customFrom, customTo])
  const summaryQuery = useQuery({
    queryKey: queryKeys.analytics(chatbotId, from, to),
    queryFn: () => api.analyticsSummary(chatbotId, from, to),
  })
  const listParams = useMemo(() => ({ from, to, q: search || undefined, status, page, pageSize: 20 }), [from, to, search, status, page])
  const listKey = useMemo(() => JSON.stringify(listParams), [listParams])
  const listQuery = useQuery({
    queryKey: queryKeys.conversations(chatbotId, listKey),
    queryFn: () => api.listConversations(chatbotId, listParams),
  })
  const detailQuery = useQuery({
    queryKey: ["chatbots", chatbotId, "conversations", selectedId ?? ""],
    queryFn: () => api.getConversation(chatbotId, selectedId!),
    enabled: Boolean(selectedId),
  })

  const summary = summaryQuery.data
  const totalPages = listQuery.data ? Math.max(1, Math.ceil(listQuery.data.total / listQuery.data.pageSize)) : 1
  const isRefreshing = summaryQuery.isFetching || listQuery.isFetching

  return (
    <div className="space-y-5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="font-display text-[19px] font-bold tracking-[-.02em]">Analytics · {chatbotName}</h2>
          <p className="muted mt-1 text-[12px]">Dados reais de uso do assistente, sem números fictícios.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 rounded-xl"
            aria-label="Atualizar dados"
            title="Atualizar dados"
            aria-busy={isRefreshing}
            disabled={isRefreshing}
            onClick={() => { void Promise.all([summaryQuery.refetch(), listQuery.refetch()]) }}
          >
            <RefreshCw className={isRefreshing ? "size-3.5 animate-spin" : "size-3.5"} />
          </Button>
          <Select value={range} onValueChange={(next) => { setRange(next as RangeKey); setPage(1) }}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl text-xs" aria-label="Período"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
          {range === "custom" && (
            <>
              <Input type="date" aria-label="De" value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); setPage(1) }} className="h-9 w-[150px] rounded-xl text-xs" />
              <Input type="date" aria-label="Até" value={customTo} onChange={(e) => { setCustomTo(e.target.value); setPage(1) }} className="h-9 w-[150px] rounded-xl text-xs" />
            </>
          )}
        </div>
      </div>

      {summaryQuery.isPending ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[92px] rounded-2xl" />)}</div>
      ) : summaryQuery.isError ? (
        <Alert variant="destructive"><AlertTriangle className="size-4" /><AlertTitle>Não foi possível carregar o analytics</AlertTitle><AlertDescription className="flex items-center justify-between gap-3"><span>{summaryQuery.error instanceof Error ? summaryQuery.error.message : "Tente novamente."}</span><Button variant="outline" size="sm" onClick={() => void summaryQuery.refetch()}><RefreshCw className="size-3.5" />Tentar novamente</Button></AlertDescription></Alert>
      ) : summary ? (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Indicadores principais">
            <StatTile label="Conversas" value={summary.totals.conversations} hint={`${summary.totals.conversationsWithMessages} com mensagens`} />
            <StatTile label="Mensagens" value={summary.totals.messages} hint={`${summary.totals.avgMessagesPerConversation} por conversa`} />
            <StatTile label="Transbordos WhatsApp" value={summary.totals.whatsappClicks} tone={summary.totals.whatsappClicks ? "success" : undefined} hint={summary.totals.conversations ? `${((summary.totals.whatsappClicks / Math.max(1, summary.totals.conversations)) * 100).toFixed(0)}% das conversas` : "Sem conversas"} />
            <StatTile label="Visitantes únicos" value={summary.totals.uniqueVisitors} hint="Sessões distintas" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="panel-bg rounded-[22px] border border-border p-5">
              <h3 className="font-display text-[15px] font-bold">Conversas ao longo do tempo</h3>
              <div className="mt-4"><BarChart data={summary.perDay} /></div>
            </section>
            <section className="panel-bg rounded-[22px] border border-border p-5">
              <h3 className="font-display text-[15px] font-bold">Balão → conversa → mensagens</h3>
              <div className="mt-4"><Funnel summary={summary} /></div>
            </section>
            <section className="panel-bg rounded-[22px] border border-border p-5">
              <h3 className="font-display text-[15px] font-bold">Temas das conversas</h3>
              <p className="muted mt-0.5 text-[11px]">Palavras mais frequentes nas mensagens dos visitantes.</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {summary.topics.length ? summary.topics.map(({ term, count }) => (
                  <span key={term} className="rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-semibold">{term} <span className="muted font-normal">· {count}</span></span>
                )) : <p className="muted text-[11px]">Sem volume suficiente ainda — aparece com as novas conversas.</p>}
              </div>
              <h3 className="font-display mt-5 text-[15px] font-bold">Links mais clicados</h3>
              <div className="mt-2 space-y-1.5">
                {summary.topLinks.length ? summary.topLinks.map(({ host, count }) => (
                  <div key={host} className="flex items-center justify-between text-xs"><span className="truncate font-medium">{host}</span><span className="muted">{count} cliques</span></div>
                )) : <p className="muted text-[11px]">Nenhum clique registrado no período.</p>}
              </div>
            </section>
            <section className="panel-bg rounded-[22px] border border-border p-5" aria-label="Tokens consumidos">
              <h3 className="font-display text-[15px] font-bold">Tokens consumidos</h3>
              <p className="muted mt-0.5 text-[11px]">Somatório do uso da OpenAI no período, por assistente.</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-border bg-muted/20 px-2 py-2.5">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="font-display mt-0.5 text-lg font-extrabold">{(summary.totals.totalTokens ?? 0).toLocaleString("pt-BR")}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 px-2 py-2.5">
                  <p className="text-xs text-muted-foreground">Entrada</p>
                  <p className="font-display mt-0.5 text-lg font-extrabold">{(summary.totals.promptTokens ?? 0).toLocaleString("pt-BR")}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 px-2 py-2.5">
                  <p className="text-xs text-muted-foreground">Saída</p>
                  <p className="font-display mt-0.5 text-lg font-extrabold">{(summary.totals.completionTokens ?? 0).toLocaleString("pt-BR")}</p>
                </div>
              </div>
            </section>
          </div>

          <div className="panel-bg flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-[22px] border border-border px-5 py-3 text-[11px] muted" aria-label="Saúde das conversas">
            <span className="flex items-center gap-1.5"><Users className="size-3.5" />{summary.totals.conversationsWithMessages} com mensagens</span>
            <span className="flex items-center gap-1.5"><MousePointerClick className="size-3.5" />{summary.totals.balloonSessions} aberturas · {summary.totals.conversionBalloonToConversation === null ? "—" : `${(summary.totals.conversionBalloonToConversation * 100).toFixed(1)}% convertem`}</span>
            <span className="flex items-center gap-1.5"><Clock className="size-3.5" />Tempo médio {formatDuration(summary.totals.avgDurationMs)}</span>
            <span className="flex items-center gap-1.5"><XCircle className="size-3.5" />{summary.totals.abandoned} abandonadas · {summary.totals.closed} encerradas</span>
            {summary.totals.errors > 0 && <span className="flex items-center gap-1.5 text-destructive"><AlertTriangle className="size-3.5" />{summary.totals.errors} com erro</span>}
          </div>
        </>
      ) : null}

      <section className="panel-bg rounded-[22px] border border-border p-5" aria-label="Histórico de conversas">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div><h3 className="font-display text-[15px] font-bold">Histórico de conversas</h3><p className="muted mt-0.5 text-[11px]">Identificação anônima da sessão; sem dados pessoais.</p></div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="bg-background flex h-9 w-full items-center rounded-xl border border-border sm:w-[220px]"><Search className="text-muted-foreground ml-2.5 size-3.5" /><Input aria-label="Buscar conversas" className="h-full w-full border-0 bg-transparent px-2 text-xs shadow-none outline-none focus-visible:ring-0" placeholder="Buscar por ID ou texto..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} /></label>
            <Select value={status} onValueChange={(next) => { setStatus(next); setPage(1) }}>
              <SelectTrigger className="h-9 w-[140px] rounded-xl text-xs" aria-label="Status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="active">Ativas</SelectItem>
                <SelectItem value="with-messages">Com mensagens</SelectItem>
                <SelectItem value="empty">Vazias</SelectItem>
                <SelectItem value="abandoned">Abandonadas</SelectItem>
                <SelectItem value="closed">Encerradas</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 rounded-xl text-xs font-semibold" asChild><a href={api.exportConversationsUrl(chatbotId, { from, to, q: search || undefined, status, format: "csv" })} download><Download className="mr-1.5 size-3.5" />CSV</a></Button>
            <Button variant="outline" size="sm" className="h-9 rounded-xl text-xs font-semibold" asChild><a href={api.exportConversationsUrl(chatbotId, { from, to, q: search || undefined, status, format: "markdown" })} download><Download className="mr-1.5 size-3.5" />Markdown</a></Button>
          </div>
        </div>

        <div className="mt-4">
          {listQuery.isPending ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : listQuery.isError ? (
            <Alert variant="destructive"><AlertTriangle className="size-4" /><AlertTitle>Não foi possível carregar as conversas</AlertTitle><AlertDescription className="flex items-center justify-between gap-3"><span>{listQuery.error instanceof Error ? listQuery.error.message : "Tente novamente."}</span><Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}><RefreshCw className="size-3.5" />Tentar novamente</Button></AlertDescription></Alert>
          ) : !listQuery.data.items.length ? (
            <p className="muted py-8 text-center text-xs">Nenhuma conversa neste filtro.</p>
          ) : (
            <>
              <ul className="divide-y divide-border">
                {listQuery.data.items.map((item) => (
                  <li key={item.id}>
                    <button type="button" onClick={() => setSelectedId(item.id)} className="flex w-full flex-col gap-1.5 px-2 py-3 text-left transition hover:bg-muted/30 sm:flex-row sm:items-center sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-semibold">#{shortId(item.id)} <span className="muted font-normal">· {item.messageCount} msgs</span></p>
                        <p className="muted truncate text-[11px]">{item.lastMessage ? item.lastMessage.content : "Sem mensagens ainda"}</p>
                      </div>
                      <span className="muted hidden text-[11px] md:block">{formatDateTime(item.startedAt)}</span>
                      <StatusBadge tone={statusTone(item.status)}>{statusLabel(item.status)}</StatusBadge>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-[11px] muted">
                <span>{listQuery.data.total} conversas · página {page} de {totalPages}</span>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="size-3.5" /></Button>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}><ChevronRight className="size-3.5" /></Button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <Dialog open={Boolean(selectedId)} onOpenChange={(open) => { if (!open) setSelectedId(null) }}>
        <DialogContent className="bg-card max-h-[85vh] overflow-hidden rounded-2xl border-border p-0 sm:max-w-[520px]">
          <DialogHeader className="border-b border-border p-5">
            <DialogTitle className="font-display text-[17px]">Conversa #{selectedId ? shortId(selectedId) : ""}</DialogTitle>
          </DialogHeader>
          <ConversationDetailView chatbotId={chatbotId} conversationId={selectedId} onClose={() => setSelectedId(null)} />
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ConversationDetailView({ chatbotId, conversationId, onClose }: { chatbotId: string; conversationId: string | null; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ["chatbots", chatbotId, "conversations", conversationId ?? ""],
    queryFn: () => api.getConversation(chatbotId, conversationId!),
    enabled: Boolean(conversationId),
  })
  if (!conversationId) return null
  if (detailQuery.isPending) return <div className="space-y-2 p-5">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
  if (detailQuery.isError || !detailQuery.data) return <div className="p-5"><Alert variant="destructive"><AlertTriangle className="size-4" /><AlertTitle>Não foi possível abrir a conversa</AlertTitle><AlertDescription>{detailQuery.error instanceof Error ? detailQuery.error.message : "Tente novamente."}</AlertDescription></Alert></div>
  const detail: ConversationDetail = detailQuery.data
  return (
    <div className="flex max-h-[60vh] flex-col">
      <div className="muted flex items-center gap-3 border-b border-border px-5 py-2.5 text-[10px]">
        <span className="flex items-center gap-1"><UserRound className="size-3" />Sessão {detail.sessionKey ? shortId(detail.sessionKey) : "—"}</span>
        <span>{formatDateTime(detail.startedAt)}</span>
      </div>
      <div className="chat-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
        {!detail.messages.length ? <p className="muted text-center text-xs">Nenhuma mensagem nesta conversa.</p> : detail.messages.map((m) => (
          m.role === "user" ? (
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2.5 text-[13px] leading-relaxed text-primary-foreground">
                {m.content}
                <span className="mt-1 block text-right text-[9px] opacity-70">{formatDateTime(m.createdAt)}</span>
              </div>
            </div>
          ) : (
            <div key={m.id} className="flex justify-start">
              <div className="bg-muted max-w-[80%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-[13px] leading-relaxed">
                {m.content}
                <span className="muted mt-1 block text-[9px]">{formatDateTime(m.createdAt)}</span>
              </div>
            </div>
          )
        ))}
      </div>
      <div className="border-t border-border p-4">
        <Button variant="outline" className="h-9 w-full rounded-xl text-xs font-semibold" onClick={onClose}><ArrowLeft className="mr-1.5 size-3.5" />Voltar à lista</Button>
      </div>
    </div>
  )
}

export function WorkspaceAnalyticsLink() {
  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2"><TrendingUp className="size-4 text-muted-foreground" /><h2 className="font-display text-[17px] font-bold">Analytics por assistente</h2></div>
      <p className="muted mt-1 text-[12px]">Abra um assistente para ver conversas, funil do balão e histórico completo. <Link to="#assistants" className="font-semibold underline">Ver assistentes</Link></p>
    </div>
  )
}
